import { Field, Form, Formik } from 'formik'
import { rem } from 'polished'
import React, { useEffect, useState } from 'react'
import {
  SESSION_SUBSCRIPTION_DATE_RANGE,
  SESSION_SUBSCRIPTION_TIME_RANGE,
} from 'shared-constants'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Button, BUTTON_VARIANT, BUTTON_INTENT } from 'components/Button'
import { CheckboxGroup } from 'components/Checkbox'
import ClientSignIn from 'components/ClientSignIn'
import ClientSignUp from 'components/ClientSignUp'
import { Delimiter } from 'components/Delimiter'
import Icon from 'components/Icon/Icon'
import { Input } from 'components/Input'
import { Flex } from 'components/Layout'
import { LinkWithText } from 'components/Link'
import { Modal } from 'components/Modal'
import { Textarea } from 'components/Textarea'
import TimezoneSelect from 'components/TimezoneSelect'
import { H6, Text } from 'components/Typography'
import { WrappedLoader } from 'components/WrappedLoader'
import { ROUTE } from 'constants/routes'
import { useUserAuth } from 'providers/userAuth'
import { AUTHENTICATION_SCREEN_TYPE } from 'providers/userAuthUtil'
import {
  SPACE,
  COLOR,
  FONT_SIZE,
  FONT_STACK,
  MODAL_MAX_WIDTH,
  HINT_HEIGHT,
  COLOR_INTENT,
} from 'Theme'
import {
  SubscriptionPreferredDateRange,
  SubscriptionPreferredTimeRange,
} from 'types/graphql-generated'
import ButtonCheckboxGroup from './ButtonCheckboxGroup'
import { GET_NOTIFIED_STEP } from './constants'

const CHECKBOX_HEIGHT = rem(30)

const StyledForm = styled(Form)`
  width: 100%;
`

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').trim().label('Email').required(),
  name: Yup.string().trim().label('Name').required(),
  areTermsAccepted: Yup.boolean().oneOf(
    [true],
    'You have to accept the terms of use'
  ),
})

interface GetNotifiedModalProps {
  onConfirmModal: (data: {
    email: string
    name: string
    description?: string
    dateRange: SubscriptionPreferredDateRange[]
    timeRange: SubscriptionPreferredTimeRange[]
    timezone: string
  }) => Promise<void>
  isModalOpen?: boolean
  onModalClose?: () => void
  isLoginLoading: boolean
  isLoading: boolean
  step: (typeof GET_NOTIFIED_STEP)[keyof typeof GET_NOTIFIED_STEP]
}

const GetNotifiedModal: React.FC<GetNotifiedModalProps> = ({
  onConfirmModal,
  onModalClose,
  isModalOpen,
  isLoginLoading,
  isLoading,
  step,
}) => {
  const userAuth = useUserAuth()
  const [authenticationScreenType, setAuthenticationScreenType] = useState(
    AUTHENTICATION_SCREEN_TYPE.SIGN_UP
  )
  useEffect(() => {
    setAuthenticationScreenType(AUTHENTICATION_SCREEN_TYPE.SIGN_IN)
  }, [userAuth.isLoading])

  let modalContent
  if (
    step === GET_NOTIFIED_STEP.AUTHENTICATION &&
    authenticationScreenType === AUTHENTICATION_SCREEN_TYPE.SIGN_UP
  ) {
    modalContent = (
      <Flex p={SPACE.PX_30} justifyContent="center">
        <Flex maxWidth={MODAL_MAX_WIDTH.PX_350} flexDirection="column">
          <Flex mb={SPACE.PX_15} flexDirection="column">
            <ClientSignUp
              onSignUpCompleted={() => {
                setAuthenticationScreenType(AUTHENTICATION_SCREEN_TYPE.SIGN_IN)
              }}
            />
          </Flex>
          <Button
            variant={BUTTON_VARIANT.MINIMAL}
            intent={BUTTON_INTENT.NONE}
            onClick={() => {
              setAuthenticationScreenType(AUTHENTICATION_SCREEN_TYPE.SIGN_IN)
            }}
          >
            Already have an account? Sign in.
          </Button>
        </Flex>
      </Flex>
    )
  }
  if (
    step === GET_NOTIFIED_STEP.AUTHENTICATION &&
    authenticationScreenType === AUTHENTICATION_SCREEN_TYPE.SIGN_IN
  ) {
    modalContent = (
      <Flex p={SPACE.PX_30} justifyContent="center">
        <Flex maxWidth={MODAL_MAX_WIDTH.PX_350} flexDirection="column">
          <Flex mb={SPACE.PX_15} flexDirection="column">
            <ClientSignIn />
          </Flex>
          <Button
            variant={BUTTON_VARIANT.MINIMAL}
            intent={BUTTON_INTENT.NONE}
            onClick={() => {
              setAuthenticationScreenType(AUTHENTICATION_SCREEN_TYPE.SIGN_UP)
            }}
          >
            Don&apos;t have an account? Sign up.
          </Button>
        </Flex>
      </Flex>
    )
  }
  if (isLoginLoading) {
    modalContent = <WrappedLoader />
  }
  if (step === GET_NOTIFIED_STEP.SUBMIT && !isLoginLoading) {
    modalContent = (
      <Flex
        p={SPACE.PX_30}
        pt={SPACE.PX_20}
        flexDirection="column"
        width="100%"
      >
        <Flex alignItems="center" mb={SPACE.PX_16}>
          <Icon.Calendar color={COLOR_INTENT.GRAY_90} />
          <Text
            fontFamily={FONT_STACK.SERIF}
            ml={SPACE.PX_8}
            color={COLOR_INTENT.GRAY_90}
          >
            Select a time that&apos;s convenient for you to discuss your
            project.
          </Text>
        </Flex>
        <Flex alignItems="center" mb={SPACE.PX_16}>
          <Icon.InfoSign color={COLOR_INTENT.GRAY_90} />
          <Text
            fontFamily={FONT_STACK.SERIF}
            ml={SPACE.PX_8}
            color={COLOR_INTENT.GRAY_90}
          >
            Share details about your project and any questions you have
            regarding the schedule.
          </Text>
        </Flex>
        <Flex alignItems="center" mb={SPACE.PX_16}>
          <Icon.Message color={COLOR_INTENT.GRAY_90} />
          <Text
            fontFamily={FONT_STACK.SERIF}
            ml={SPACE.PX_8}
            color={COLOR_INTENT.GRAY_90}
          >
            You&apos;ll receive a confirmation and response via email, usually
            within a few days
          </Text>
        </Flex>

        <Delimiter color={COLOR_INTENT.GRAY_10} />

        <H6 color={COLOR_INTENT.GRAY_90} mt={SPACE.PX_24}>
          Choose Your Preferred Time
        </H6>

        <Formik
          initialValues={{
            timezone: (userAuth.user?.timezone as string) ?? '',
            email: (userAuth.user?.contactEmail as string) ?? '',
            name: (userAuth.user?.fullName as string) ?? '',
            areTermsAccepted: !!userAuth.user?.contactEmail,
            dateRange: [SubscriptionPreferredDateRange.ThisWeek],
            timeRange: [SubscriptionPreferredTimeRange.Morning],
          }}
          validationSchema={validationSchema}
          onSubmit={onConfirmModal}
        >
          {({ handleSubmit, setFieldValue }) => (
            <StyledForm onSubmit={handleSubmit}>
              <ButtonCheckboxGroup
                label="When would you like to schedule your consultation?"
                name="dateRange"
                items={[
                  {
                    label: 'This week',
                    value: SESSION_SUBSCRIPTION_DATE_RANGE.THIS_WEEK,
                  },
                  {
                    label: 'Next two weeks',
                    value: SESSION_SUBSCRIPTION_DATE_RANGE.NEXT_TWO_WEEKS,
                  },
                  {
                    label: 'Next month',
                    value: SESSION_SUBSCRIPTION_DATE_RANGE.NEXT_MONTH,
                  },
                  {
                    label: 'Next 6 months',
                    value: SESSION_SUBSCRIPTION_DATE_RANGE.NEXT_SIX_MONTHS,
                  },
                ]}
                onChange={(value) => setFieldValue('dateRange', value)}
              />
              <ButtonCheckboxGroup
                label="Preferred Time of Day"
                name="timeRange"
                items={[
                  {
                    label: 'Morning',
                    value: SESSION_SUBSCRIPTION_TIME_RANGE.MORNING,
                  },
                  {
                    label: 'Afternoon',
                    value: SESSION_SUBSCRIPTION_TIME_RANGE.AFTERNOON,
                  },
                  {
                    label: 'Evening',
                    value: SESSION_SUBSCRIPTION_TIME_RANGE.EVENING,
                  },
                ]}
                onChange={(value) => setFieldValue('timeRange', value)}
              />
              <Field name="timezone">
                {({ field: { onChange, value, ...restField }, meta }) => (
                  <TimezoneSelect
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched && meta.error ? meta.error : null}
                    onChange={(newValue) => {
                      setFieldValue(restField.name, newValue)
                      onChange(newValue)
                    }}
                    hasHint
                    menuPortalTarget={document.body}
                    value={value}
                    {...restField}
                  />
                )}
              </Field>
              <H6 color={COLOR_INTENT.GRAY_90} mb={SPACE.PX_24}>
                Contact
              </H6>

              <Field name="name">
                {({ field, meta }) => (
                  <Input
                    label="Your name"
                    isDisabled={userAuth.isLoading || userAuth.isClientSignedIn}
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched ? meta.error : ''}
                    hasHint
                    {...field}
                  />
                )}
              </Field>
              <Field name="email">
                {({ field, meta }) => (
                  <Input
                    label="Email"
                    isDisabled={userAuth.isLoading || userAuth.isClientSignedIn}
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched ? meta.error : ''}
                    hasHint
                    {...field}
                  />
                )}
              </Field>

              <H6 color={COLOR_INTENT.GRAY_90}>
                Tell us more about your project
              </H6>

              <Field name="description">
                {({ field, meta }) => (
                  <Textarea
                    label="(Optional)" // I don't know how to display that counter without truthy label
                    maxLength={500}
                    rows={5}
                    isError={meta.touched && meta.error !== undefined}
                    hint={meta.touched ? meta.error : ''}
                    hasHint
                    {...field}
                  />
                )}
              </Field>
              <Delimiter color={COLOR_INTENT.GRAY_10} my={SPACE.PX_24} />
              {!userAuth.isLoading && !userAuth.isClientSignedIn && (
                <CheckboxGroup>
                  <Field type="checkbox" name="areTermsAccepted">
                    {({ field: { checked, ...restField }, meta }) => (
                      <Flex
                        flexDirection="column"
                        height={`calc(${CHECKBOX_HEIGHT} + ${SPACE.PX_5} + ${HINT_HEIGHT})`}
                      >
                        <CheckboxGroup.Item isChecked={checked} {...restField}>
                          I accept{' '}
                          <LinkWithText href={ROUTE.TERMS_OF_USE()}>
                            the terms of use
                          </LinkWithText>
                        </CheckboxGroup.Item>
                        <Text
                          fontSize={FONT_SIZE.PX_12}
                          color={COLOR.SECONDARY_1}
                        >
                          {meta.touched && meta.error}
                        </Text>
                      </Flex>
                    )}
                  </Field>
                </CheckboxGroup>
              )}
              <Flex mt={SPACE.PX_10} justifyContent="flex-end">
                <Button
                  variant={BUTTON_VARIANT.OUTLINE}
                  width="initial"
                  onClick={onModalClose}
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  mt={SPACE.PX_20}
                >
                  Cancel
                </Button>
                <Flex ml={SPACE.PX_10} />
                <Button
                  width="initial"
                  type="submit"
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  mt={SPACE.PX_20}
                >
                  Send request
                </Button>
              </Flex>
            </StyledForm>
          )}
        </Formik>
      </Flex>
    )
  }
  let modalLabel = 'Request Availability'
  if (
    step === GET_NOTIFIED_STEP.AUTHENTICATION &&
    authenticationScreenType === AUTHENTICATION_SCREEN_TYPE.SIGN_UP
  ) {
    modalLabel = 'Sign up'
  }
  if (
    step === GET_NOTIFIED_STEP.AUTHENTICATION &&
    authenticationScreenType === AUTHENTICATION_SCREEN_TYPE.SIGN_IN
  ) {
    modalLabel = 'Sign in'
  }
  return (
    <Modal
      isWithFooter={false}
      isWithoutPaddings
      ariaLabel={modalLabel}
      isOpen={isModalOpen}
      onModalClose={onModalClose}
      title={modalLabel}
      width="100%"
      maxHeight="90vh"
      maxWidth={MODAL_MAX_WIDTH.PX_720}
      data-test-id={DATA_TEST_ID.GET_NOTIFIED_MODAL}
    >
      {modalContent}
    </Modal>
  )
}

export default GetNotifiedModal
